import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StatsGranularity } from '../../services/types';
import {
  formatGermanNumber,
  formatPeriodLong,
  formatPeriodShort,
  isoWeekYearOf,
} from '../../utils/statisticsFormat';
import { mergeSeriesRows } from './statSeries';
import type { StatSeries } from './statSeries';

/** Chart-Hoehe in Pixeln (alle Dashboard-Charts einheitlich). */
export const CHART_HEIGHT = 280;

/* Chart-Chrome (dezent, Projekt-Design-Review): */
/** Farbe der horizontalen Gridlines. */
const GRID_COLOR = '#e1e0d9';
/** Gedaempfte Farbe fuer Achsen- und Tick-Beschriftung. */
const TICK_COLOR = '#898781';
/** Zurueckhaltende Basislinie der x-Achse. */
const BASELINE_COLOR = '#c3c2b7';
/** Hover-Hinterlegung des Balken-Cursors. */
const BAR_CURSOR_FILL = 'rgba(11, 11, 11, 0.04)';

/** Gemeinsamer Tick-Stil beider Achsen. */
const AXIS_TICK_STYLE = { fill: TICK_COLOR, fontSize: 12 } as const;

/** Props der TimeSeriesChart-Komponente. */
interface TimeSeriesChartProps {
  /** ID-Praefix des zugehoerigen Charts (z. B. "chart-registrations"). */
  readonly chartId: string;
  /** Darstellungsart: Balken oder Linie. */
  readonly variant: 'bar' | 'line';
  /** Granularitaet (steuert die Perioden-Formatierung). */
  readonly granularity: StatsGranularity;
  /** Eine oder zwei Serien; Farbe folgt der Serie, nie der Position. */
  readonly series: readonly StatSeries[];
}

/** Formatiert einen Tooltip-Wert im deutschen Zahlenformat. */
function formatTooltipValue(value: unknown): string {
  return typeof value === 'number' ? formatGermanNumber(value) : String(value ?? '');
}

/**
 * Formatiert einen y-Achsen-Tick im deutschen Zahlenformat.
 * Eigene Funktion (nicht der Tooltip-Formatter): Achse und Tooltip sind
 * unterschiedliche Aufgaben und duerfen sich unabhaengig entwickeln.
 */
function formatAxisTick(value: unknown): string {
  return typeof value === 'number' ? formatGermanNumber(value) : String(value ?? '');
}

/**
 * Prop-gesteuertes Zeitreihen-Chart (recharts) fuer das Dashboard.
 *
 * Gestaltungsvorgaben (Projekt-Design-Review, verbindlich):
 * - EINE y-Achse, beginnend bei 0; nur horizontale, dezente Gridlines
 * - Balken schmal mit oben abgerundeten Ecken, 2px Luecke zwischen zwei
 *   Serien; Linien mit strokeWidth 2, Punkte erst bei Hover
 * - Tooltip immer aktiv (Cursor-Linie bei Line, Hinterlegung bei Bar),
 *   Werte im deutschen Zahlenformat
 * - Legende NUR bei zwei Serien (eine Serie benennt der Card-Titel)
 */
function TimeSeriesChart({
  chartId,
  variant,
  granularity,
  series,
}: TimeSeriesChartProps): React.JSX.Element {
  const showLegend = series.length > 1;
  /*
   * Nur im Mehr-Serien-Chart fehlende Perioden mit 0 auffuellen: recharts
   * laesst eine fehlende Serie im Tooltip sonst kommentarlos weg, was von
   * "0" nicht zu unterscheiden ist. Die Tabelle bleibt bewusst bei "–",
   * weil sie "kein Wert" explizit anzeigen kann.
   */
  const rows = useMemo(
    () => mergeSeriesRows(series, { fillMissingWithZero: showLegend }),
    [series, showLegend]
  );

  /*
   * Wochen-Ticks brauchen das ISO-Jahr, sobald der dargestellte Zeitraum
   * mehr als ein ISO-Jahr umfasst — sonst stuende "KW 32" mehrfach auf der
   * Achse. Tooltip und Tabelle behalten immer die Langform "KW 32 2026".
   */
  const includeWeekYear = useMemo(() => {
    if (granularity !== 'week') {
      return false;
    }
    const isoYears = new Set<number>();
    for (const row of rows) {
      const isoYear = isoWeekYearOf(row.periodStart);
      if (isoYear !== null) {
        isoYears.add(isoYear);
      }
      if (isoYears.size > 1) {
        return true;
      }
    }
    return false;
  }, [rows, granularity]);

  /**
   * Gemeinsame Chart-Kinder (Grid, Achsen, Tooltip, optionale Legende).
   * Als Element-Array, damit Bar- und Line-Variante identisch bleiben
   * (recharts erwartet seine Bausteine als direkte Kinder).
   */
  const commonChildren: React.ReactElement[] = [
    <CartesianGrid key="grid" stroke={GRID_COLOR} vertical={false} />,
    <XAxis
      key="x-axis"
      dataKey="periodStart"
      tick={AXIS_TICK_STYLE}
      tickLine={false}
      axisLine={{ stroke: BASELINE_COLOR }}
      tickFormatter={(value) => formatPeriodShort(String(value), granularity, { includeWeekYear })}
      minTickGap={16}
    />,
    <YAxis
      key="y-axis"
      allowDecimals={false}
      domain={[0, 'auto']}
      /* 56px: sechsstellige deutsche Zahlen ("123.456") passen ohne Abschnitt. */
      width={56}
      tick={AXIS_TICK_STYLE}
      tickLine={false}
      axisLine={false}
      tickFormatter={(value) => formatAxisTick(value)}
    />,
    <Tooltip
      key="tooltip"
      cursor={
        variant === 'line'
          ? { stroke: BASELINE_COLOR, strokeDasharray: '3 3' }
          : { fill: BAR_CURSOR_FILL }
      }
      formatter={(value) => formatTooltipValue(value)}
      labelFormatter={(label) => formatPeriodLong(String(label), granularity)}
    />,
  ];
  if (showLegend) {
    commonChildren.push(
      <Legend
        key="legend"
        verticalAlign="top"
        align="right"
        iconType="circle"
        iconSize={8}
        wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
      />
    );
  }

  return (
    <div id={`${chartId}-chart`}>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        {variant === 'bar' ? (
          <BarChart data={rows} barGap={2} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            {commonChildren}
            {series.map((singleSeries) => (
              <Bar
                key={singleSeries.seriesKey}
                dataKey={singleSeries.seriesKey}
                name={singleSeries.label}
                fill={singleSeries.color}
                radius={[4, 4, 0, 0]}
                maxBarSize={24}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        ) : (
          <LineChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            {commonChildren}
            {series.map((singleSeries) => (
              <Line
                key={singleSeries.seriesKey}
                type="monotone"
                dataKey={singleSeries.seriesKey}
                name={singleSeries.label}
                stroke={singleSeries.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

export default TimeSeriesChart;
